// Standalone agent-payments page, extracted out of the main wallet UI (per #product-wallet).
// Reachable from the CLI + the kebab (⋯) menu only — NOT in the top-level sidebar nav.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Tabs, useCopy } from 'regen-ui'
import { toast } from 'sonner'
import { formatUnits } from 'viem'
import { useConnection } from 'wagmi'
import { tempo } from 'wagmi/chains'
import ArrowUpRight from '~icons/lucide/arrow-up-right'
import Check from '~icons/lucide/check'
import Copy from '~icons/lucide/copy'

import { agents, type AgentId, SKILL_URL } from '#/lib/agents.js'
import { useAssets } from '#/lib/assets.js'
import * as Auth from '#/lib/auth.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { readOnly } from '#/lib/connectors/readOnly.js'
import { formatCreditCents, useCreditsBalance } from '#/lib/credits.js'
import * as Currency from '#/lib/currency.js'
import { useFundingOptions } from '#/lib/funding-options.js'
import * as Valuation from '#/lib/valuation.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'

export const Route = createFileRoute('/_app/agent')({ component: AgentPage })

const money = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
})

function AgentPage() {
  const me = Auth.me.useQuery()
  const connection = useConnection()
  const { address: connectedAddress, chainId } = connection
  const isViewOnly = connection.connector?.type === readOnly.type
  const address = isViewOnly ? connectedAddress : (me.data?.address ?? connectedAddress)
  const queryClient = useQueryClient()
  const connector = useDialogConnector()

  // Stablecoin balance — mirrors the home dashboard's USD total across held assets.
  const hintTokens =
    chainId === tempo.id
      ? (['0x20C000000000000000000000b9537d11c60E8b50'] as const)
      : ([
          '0x20c0000000000000000000000000000000000000',
          '0x20c0000000000000000000000000000000000001',
          '0x20c0000000000000000000000000000000000002',
          '0x20c0000000000000000000000000000000000003',
        ] as const)
  const assets = useAssets({
    account: address,
    chainId,
    enabled: typeof window !== 'undefined' && !!address,
    fresh: true,
    tokens: hintTokens,
  })
  const valuation = Valuation.useValuation({
    account: address,
    chainId,
    enabled: typeof window !== 'undefined' && !!address,
  })
  const stablecoinTotal = useMemo(
    () =>
      Valuation.total({
        assets: assets.data ?? [],
        fallback: (assets.data ?? []).reduce((sum, asset) => {
          if (Currency.inferCurrency(asset.symbol) !== 'USD') return sum
          const zoneSum = (asset.zones ?? []).reduce((s, z) => s + BigInt(z.balance), 0n)
          const gross = BigInt(asset.balance) + zoneSum
          return sum + Number(formatUnits(gross, asset.decimals))
        }, 0),
        valuation: valuation.data,
      }),
    [assets.data, valuation.data?.amount, valuation.data?.available],
  )

  // MPP Credits — optional, Coinflow-gated.
  const fundingOptions = useFundingOptions()
  const creditsEnabled = !!address && chainId === tempo.id && fundingOptions.data?.coinflow === true
  const credits = useCreditsBalance({ enabled: creditsEnabled })

  const fundStables = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return provider.request({ method: 'wallet_deposit', params: [{}] })
    },
    onSuccess: () => {
      toast.success('Funds added')
      queryClient.invalidateQueries()
    },
  })
  const fundCredits = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return provider.request({ method: 'wallet_deposit', params: [{ intent: 'credits' }] })
    },
    onSuccess: () => {
      toast.success('Credits added')
      queryClient.invalidateQueries()
    },
  })

  // Auto-open the deposit dialog when navigated to with `?action=fund` (e.g. the
  // Tempo CLI funding handoff), honoring `intent` (credits vs stablecoins), then
  // strip the params. Wait for `address` so the dialog connector has a session.
  const search = useSearch({ strict: false }) as { action?: string; intent?: string }
  const navigate = useNavigate()
  const triggered = useRef(false)
  useEffect(() => {
    if (search.action !== 'fund' || isViewOnly || !address || triggered.current) return
    triggered.current = true
    navigate({
      to: '/agent',
      search: (prev) => ({ ...prev, action: undefined, intent: undefined }),
      replace: true,
    })
    if (search.intent === 'credits') fundCredits.mutate()
    else fundStables.mutate()
  }, [search.action, search.intent, isViewOnly, address])

  return (
    <ScreenLayout
      walletActions={false}
      heading={
        <>
          <Link
            className="inline-flex w-fit items-center gap-1 text-label-13 text-foreground-secondary hover:text-foreground"
            to="/"
          >
            ← Wallet
          </Link>
          <h1 className="text-heading-32 text-foreground">Agent payments</h1>
          <p className="max-w-[560px] text-label-14 text-foreground-secondary">
            Let your agent pay as it goes over the Machine Payments Protocol (MPP) — from your
            stablecoin balance, or from prepaid credits. This mostly lives in the CLI; the web UI is
            just an escape hatch for funding and connecting.
          </p>
        </>
      }
    >
      <div className="flex max-w-[760px] flex-col gap-8">
        {/* 1 — Balances: stablecoins (primary) + credits (optional) */}
        <Box className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-0.5">
              <p className="text-label-13 text-foreground-secondary">Balance</p>
              <p
                className="text-heading-24 text-foreground tabular-nums"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {!address ? (
                  '—'
                ) : assets.isLoading ? (
                  <span className="inline-block h-[1em] w-24 animate-pulse rounded-body bg-skeleton align-middle" />
                ) : (
                  money.format(stablecoinTotal)
                )}
              </p>
            </div>
            <Button
              loading={fundStables.isPending}
              onClick={() => fundStables.mutate()}
              variant="primary"
            >
              Add funds
            </Button>
          </div>

          <div className="h-px w-full bg-border" />

          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-0.5">
              <p className="text-label-13 text-foreground-secondary">MPP Credits · optional</p>
              <p
                className="text-label-16 text-foreground tabular-nums"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCreditCents(credits.data?.credits.cents ?? 0)}
              </p>
            </div>
            <Button
              loading={fundCredits.isPending}
              onClick={() => fundCredits.mutate()}
              variant="secondary"
            >
              Buy credits
            </Button>
          </div>
          <Link
            className="inline-flex w-fit items-center gap-1 text-label-13 text-foreground-secondary hover:text-foreground"
            to="/credits/activity"
          >
            View credit activity <ArrowUpRight className="size-3.5" />
          </Link>
        </Box>

        {/* 2 — Connect your agent (single selector → one command → one copy) */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-label-16 font-medium text-foreground">Connect your agent</h2>
            <p className="text-label-13 text-foreground-secondary">
              Pick your agent and run this — it installs the Tempo CLI and connects to this wallet
              so it can pay from your balance.
            </p>
          </div>
          <ConnectPanel />
          <div className="mt-4 flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-4">
              <p className="text-label-14 font-medium text-foreground">
                Give your agent new capabilities
              </p>
              <a
                className="inline-flex shrink-0 items-center gap-1 text-label-13 text-foreground-secondary hover:text-foreground"
                href="https://mpp.dev/services"
                rel="noopener noreferrer"
                target="_blank"
              >
                View all services <ArrowUpRight className="size-3.5" />
              </a>
            </div>
            <p className="text-label-13 text-foreground-secondary">
              Once connected, just ask — e.g. "find me a flight to NYC", "research recent AI
              funding", "order more printer ink". It pays as it goes.
            </p>
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}

/** Landing-page-style command panel: pick your agent, copy one connect command. */
function ConnectPanel() {
  const [agentId, setAgentId] = useState<AgentId>('claude')
  const agent = agents.find((a) => a.id === agentId) ?? agents[0]
  const command = `${agent.prefix} "Read ${SKILL_URL} and connect to my Tempo wallet"`
  const { copy, notifying: copied } = useCopy()

  return (
    <div className="rounded-body border border-border bg-surface">
      <div className="flex items-stretch gap-3 border-b border-border px-4">
        <div className="hidden items-center gap-1.5 sm:flex" aria-hidden>
          <span className="size-2.5 rounded-full bg-accent/35" />
          <span className="size-2.5 rounded-full bg-accent/35" />
          <span className="size-2.5 rounded-full bg-accent/35" />
        </div>
        <Tabs
          active={agentId}
          className="min-w-0 flex-1 border-b-0"
          onChange={setAgentId}
          tabs={agents}
        />
      </div>

      <div className="flex w-full items-start gap-3 p-4 text-left">
        <span className="shrink-0 pt-0.5 font-mono text-label-13 text-foreground-secondary">$</span>
        <code className="min-w-0 flex-1 whitespace-pre-wrap break-words font-mono text-label-13 text-foreground">
          <span className="text-foreground">{agent.prefix}</span>{' '}
          <span className="text-foreground-secondary">
            "Read {SKILL_URL} and connect to my Tempo wallet"
          </span>
        </code>
      </div>

      <div className="border-t border-border">
        <Button
          className="w-full rounded-t-none rounded-b-body"
          icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          onClick={() => copy(command)}
          type="button"
          variant="primary"
        >
          {copied ? 'Copied' : 'Connect your agent'}
        </Button>
      </div>
    </div>
  )
}
